import { Link } from 'react-router-dom';
import type { Series } from '@/mocks/series';
import { useFavorites } from '@/hooks/useFavorites';

interface SeriesCardProps {
  series: Series;
  rank?: number;
}

export default function SeriesCard({ series, rank }: SeriesCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favored = isFavorite(series.id);

  const handleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(series.id);
  };

  return (
    <article className="group">
      <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
        <Link to={`/detail/${series.id}`} aria-label={`查看《${series.title}》详情`} className="block w-full h-full">
          <img
            src={series.poster}
            alt={`${series.title} 剧集海报，${series.genres.join('、')}题材电视剧`}
            title={`${series.title} 在线观看 高清电视剧`}
            className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/10 to-transparent" />

          {typeof rank === 'number' && (
            <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-br-lg bg-primary-500 font-label text-sm font-semibold text-background-50">
              {rank}
            </span>
          )}

          <span className="absolute bottom-2 left-2 rounded-md bg-background-50/90 px-2 py-0.5 font-label text-xs font-medium text-primary-700 backdrop-blur">
            {series.status}
          </span>
        </Link>

        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 font-label text-xs font-semibold text-background-50">
          <i className="ri-star-fill text-[11px]" />
          {series.rating.toFixed(1)}
        </span>

        <button
          type="button"
          onClick={handleFavorite}
          aria-label={favored ? `取消收藏《${series.title}》` : `收藏《${series.title}》`}
          className={`absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full backdrop-blur transition-colors ${
            favored
              ? 'bg-accent-500 text-background-50'
              : 'bg-foreground-950/45 text-background-50 hover:bg-accent-500'
          }`}
        >
          <i className={favored ? 'ri-heart-fill' : 'ri-heart-line'} />
        </button>
      </div>

      <h3 className="mt-3 truncate text-sm font-medium text-foreground-950 md:text-base">
        <Link to={`/detail/${series.id}`} className="hover:text-primary-600">
          {series.title}
        </Link>
      </h3>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
        <span className="text-secondary-600">{series.region}</span>
        <span>{series.year}</span>
        <span>{series.genres[0]}</span>
      </p>
    </article>
  );
}